'use client';
import { useMemo } from 'react';
import { useMe } from '../../lib/queries';
import { usePersistentState } from '../../lib/usePersistentState';
import {
  EMPTY_CONVERSATION_FILTERS, type ConversationListFilters, type ConversationListSort, type ConversationListSortKey,
  type ConversationListStatus, type ConversationListView,
} from './conversationListModel';

interface ConversationListPrefs { filters: ConversationListFilters; sort: ConversationListSort }

const DEFAULT_SORT: ConversationListSort = { key: 'updated', direction: 'desc' };
const DEFAULT_PREFS: ConversationListPrefs = { filters: EMPTY_CONVERSATION_FILTERS, sort: DEFAULT_SORT };

const STATUSES: readonly ConversationListStatus[] = ['working', 'waiting', 'unread', 'error'];
const SORT_KEYS: readonly ConversationListSortKey[] = ['updated', 'tokens', 'title', 'state', 'model', 'owner'];

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');

/** The stored blob in the exact shape the filters accept, or null. Mine has no owner facet or owner sort,
 *  so a value for either there would be an invisible active filter and is refused like any other unknown. */
function parsePrefs(raw: string, view: ConversationListView): ConversationListPrefs | null {
  let value: unknown;
  try { value = JSON.parse(raw); } catch { return null; }
  if (typeof value !== 'object' || value === null) return null;
  const { filters, sort } = value as { filters?: Partial<Record<keyof ConversationListFilters, unknown>>; sort?: { key?: unknown; direction?: unknown } };
  if (!filters || !sort) return null;
  const { projects, statuses, models, types, owners } = filters;
  if (!isStringArray(projects) || !isStringArray(models) || !isStringArray(types)) return null;
  if (!isStringArray(statuses) || !statuses.every((status): status is ConversationListStatus => (STATUSES as readonly string[]).includes(status))) return null;
  if (!Array.isArray(owners) || !owners.every(owner => Number.isInteger(owner))) return null;
  if (view === 'mine' && owners.length > 0) return null;
  if (!SORT_KEYS.includes(sort.key as ConversationListSortKey) || (sort.direction !== 'asc' && sort.direction !== 'desc')) return null;
  if (view === 'mine' && sort.key === 'owner') return null;
  return { filters: { projects, statuses, models, types, owners: owners as number[] }, sort: { key: sort.key as ConversationListSortKey, direction: sort.direction } };
}

/** Filters and sort of the conversation list, remembered per browser, account and view. Search text and
 *  page are not kept: they describe one visit, while the facets are how the reader wants the list cut.
 *  Mine and All keep separate slots because they offer different facets. */
export function useConversationListPrefs(view: ConversationListView) {
  const accountId = useMe().data?.user?.id;
  const key = `elowen.conversationList.prefs.${accountId ?? 'anonymous'}.${view}`;
  const [raw, setRaw] = usePersistentState<string>(key, JSON.stringify(DEFAULT_PREFS), value => parsePrefs(value, view) !== null);
  const prefs = useMemo(() => parsePrefs(raw, view) ?? DEFAULT_PREFS, [raw, view]);
  return {
    filters: prefs.filters,
    sort: prefs.sort,
    setFilters: (filters: ConversationListFilters) => setRaw(JSON.stringify({ filters, sort: prefs.sort })),
    setSort: (sort: ConversationListSort) => setRaw(JSON.stringify({ filters: prefs.filters, sort })),
  };
}
